/**
 * secureStorage.js
 *
 * Centralized secure storage utility.
 * Uses sessionStorage (cleared on tab/browser close) for auth tokens,
 * providing better XSS protection than localStorage.
 *
 * Design: all auth state is keyed consistently, token expiry is
 * validated on every read, and a central "clear all" is provided
 * for logout flows.
 */

const KEYS = {
  TOKEN: 'wc_token',
  USER: 'wc_user',
  ADMIN_TOKEN: 'wc_admin_token',
  ADMIN_USER: 'wc_admin_user',
};

/**
 * Decode a JWT payload without verifying the signature.
 * Signature verification is done server-side — we use this only
 * to read the `exp` claim for client-side expiry checks.
 */
function decodeJWT(token) {
  try {
    const base64Payload = token.split('.')[1];
    if (!base64Payload) return null;
    const json = atob(base64Payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Check whether a JWT token has expired.
 * Returns true if expired or malformed.
 */
function isTokenExpired(token) {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) return true;
  // Add a 30-second buffer to account for clock skew
  return Date.now() / 1000 > payload.exp - 30;
}

/**
 * Get a token from sessionStorage.
 * Automatically removes and returns null if the token has expired.
 */
function getToken(adminMode = false) {
  const key = adminMode ? KEYS.ADMIN_TOKEN : KEYS.TOKEN;
  const token = sessionStorage.getItem(key);
  if (!token) return null;
  if (isTokenExpired(token)) {
    sessionStorage.removeItem(key);
    return null;
  }
  return token;
}

/**
 * Store a token in sessionStorage.
 */
function setToken(token, adminMode = false) {
  const key = adminMode ? KEYS.ADMIN_TOKEN : KEYS.TOKEN;
  sessionStorage.setItem(key, token);
}

/**
 * Get user data from sessionStorage.
 * Strips any sensitive fields before returning.
 */
function getUser(adminMode = false) {
  const key = adminMode ? KEYS.ADMIN_USER : KEYS.USER;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const user = JSON.parse(raw);
    // Paranoia check: never expose password even if it somehow got stored
    const { password, ...safeUser } = user;
    return safeUser;
  } catch {
    return null;
  }
}

/**
 * Store user data in sessionStorage.
 * Strips password before storing.
 */
function setUser(userData, adminMode = false) {
  const key = adminMode ? KEYS.ADMIN_USER : KEYS.USER;
  if (!userData) return;
  const { password, ...safeUser } = userData;
  sessionStorage.setItem(key, JSON.stringify(safeUser));
}

/**
 * Clear all auth-related storage (used on logout).
 */
function clearAll() {
  Object.values(KEYS).forEach(key => sessionStorage.removeItem(key));
}

/**
 * Check if any valid (non-expired) auth token exists.
 */
function isAuthenticated(adminMode = false) {
  return getToken(adminMode) !== null;
}

/**
 * Get the role from the stored token payload.
 */
function getRole(adminMode = false) {
  const token = getToken(adminMode);
  if (!token) return null;
  const payload = decodeJWT(token);
  return payload?.role || null;
}

const SecureStorage = {
  getToken,
  setToken,
  getUser,
  setUser,
  clearAll,
  isAuthenticated,
  isTokenExpired,
  decodeJWT,
  getRole,
};

export default SecureStorage;
